import { Link } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft } from 'lucide-react'

type LicenseRef = {
  id: string
  display_name: string
  type_name: string
  business_name: string
}

type RecordRef = {
  id: string
  period_start_date: string
  period_end_date: string
}

type FilingDetail = {
  id: string
  reference_number: string | null
  submitted_at: string | null
  accepted: boolean
  rejected: boolean
  pending: boolean
  rejection_reason: string | null
} | null

type Props = {
  license: LicenseRef
  record: RecordRef
  filing: FilingDetail
}

export default function BackofficeLicenseRecordFilingShow({ license, record, filing }: Props) {
  return (
    <BackofficeLayout
      title="Filing Status"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Licenses', href: '/backoffice/licenses' },
        { label: license.display_name, href: `/backoffice/licenses/${license.id}` },
        { label: `${record.period_start_date} - ${record.period_end_date}`, href: `/backoffice/licenses/${license.id}/records/${record.id}` },
        { label: 'Filing' },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Filing Status</h1>
          <p className="text-sm text-muted-foreground">{license.business_name} · {record.period_start_date} — {record.period_end_date}</p>
        </div>

        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Government Filing</h2>
            {filing && (
              <div className="flex items-center gap-2">
                {filing.accepted && <Badge className="bg-green-600 text-white font-normal text-xs">Accepted</Badge>}
                {filing.rejected && <Badge className="bg-red-600 text-white font-normal text-xs">Rejected</Badge>}
                {filing.pending && <Badge className="bg-blue-600 text-white font-normal text-xs">Pending</Badge>}
              </div>
            )}
          </div>
          <div className="px-4 py-3">
            {filing ? (
              <div className="space-y-1.5">
                {filing.reference_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Reference</span>
                    <span>{filing.reference_number}</span>
                  </div>
                )}
                {filing.submitted_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Submitted</span>
                    <span>{new Date(filing.submitted_at).toLocaleDateString()}</span>
                  </div>
                )}
                {filing.rejection_reason && (
                  <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                    {filing.rejection_reason}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No filing submitted yet</p>
            )}
          </div>
        </section>

        <div>
          <Button variant="outline" asChild>
            <Link href={`/backoffice/licenses/${license.id}/records/${record.id}`}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Record
            </Link>
          </Button>
        </div>
      </div>
    </BackofficeLayout>
  )
}
